import { emptyFragment } from '@execonline-inc/execonline-ui';
import { isJust, Maybe as StructuredMaybe } from '@execonline-inc/structured-maybe';
import { assertNever } from '@kofno/piper';
import { Maybe } from 'maybeasy';
import { observer } from 'mobx-react';
import * as React from 'react';
import { T } from '../../../../../Translations';
import InlineSpinner from '../../../../Cutesies/InlineSpinner';
import { familyCourseOptions, optionForFamily } from '../CourseField/CourseOptions';
import { shareStore } from '../Stores/ShareStore';
import { Program, ShareBy, ShareType } from '../Stores/ShareStore/Types';
import DoneButton from './DoneButton';
import NextButton from './NextButton';
import ParticipantAccessToggle from './ParticipantAccessToggle';
import ShareMethodSelector from './ShareMethodSelector';
import ShareTypeSelector from './ShareTypeSelector';
import ShareUrl from './ShareUrl';

// The dialog is one size in every state, loading and saving included
// (DVLP-7559, DVLP-8217). The frame is as tall as the tallest resting state,
// Share by URL with a course chosen (card with one row of start-date chips,
// URL, Copy URL), so the others pad up to it. On a window too short for that
// it shrinks to fit: 4rem is the room the dialog keeps around itself and
// 128px its padding, title and the gap above the frame. Only the body
// scrolls, so Done and Next stay in view.
const frameClassName = 'flex h-[min(840px,calc(100vh_-_4rem_-_128px))] flex-col';

interface Props {
  collectionId: number;
  shareBy: ShareBy;
  setShareBy: (shareBy: ShareBy) => void;
  shareType: ShareType;
  setShareType: (shareType: ShareType) => void;
  selectedProgramFamilyId: Maybe<number>;
  setSelectedProgramFamilyId: (programFamilyId: Maybe<number>) => void;
  close: () => void;
  publishedAt: StructuredMaybe<Date>;
  onTogglePublished: () => void;
  isPublishing: boolean;
  returnPath?: string;
}

const Content: React.FC<Props> = ({
  collectionId,
  shareBy,
  setShareBy,
  shareType,
  setShareType,
  selectedProgramFamilyId,
  setSelectedProgramFamilyId,
  close,
  publishedAt,
  onTogglePublished,
  isPublishing,
  returnPath,
}) => {
  // Check if collection is published to determine toggle state
  const isPublished = isJust(publishedAt);

  // Handler for program family selection (URL flow) - fetches ShareUrl
  const handleProgramFamilyChange = (programFamilyId: Maybe<number>) => {
    setSelectedProgramFamilyId(programFamilyId);

    // Fetch program family ShareUrl when selection changes
    programFamilyId.cata({
      Just: (id) => {
        shareStore.fetchProgramShareUrl(id);
      },
      Nothing: () => {
        shareStore.clearProgramShareUrl();
      },
    });
  };

  // Switching to program access preselects nothing: the course is chosen on
  // the picker below (URL) or on the invite page (email), DVLP-8060.
  const handleShareTypeChange = (newShareType: ShareType) => {
    setShareType(newShareType);

    if (newShareType === 'collection') {
      shareStore.clearProgramShareUrl();
    }
  };

  // The URL to copy exists once there is something to point it at: the
  // collection, or the course the admin has chosen. The course counts only
  // if the collection still offers it, the same test the card makes.
  const hasShareTarget = (programs: ReadonlyArray<Program>): boolean => {
    switch (shareType) {
      case 'collection':
        return true;
      case 'program':
        return selectedProgramFamilyId
          .andThen((id) => optionForFamily(familyCourseOptions(programs), id))
          .isJust();
      default:
        return assertNever(shareType);
    }
  };

  switch (shareStore.state.kind) {
    case 'waiting':
    case 'loading':
      return (
        <div className={frameClassName}>
          <div className="flex flex-1 items-center justify-center">
            <InlineSpinner size={48} />
          </div>
        </div>
      );
    case 'publishing':
      return (
        <div className={frameClassName}>
          <ParticipantAccessToggle
            isPublished={isPublished}
            onTogglePublished={onTogglePublished}
            isPublishing={isPublishing}
          />

          <hr className="border-border mb-8" />

          <div className="flex flex-1 items-center justify-center">
            <InlineSpinner size={48} />
          </div>
        </div>
      );
    case 'refreshing':
    case 'reviewing':
    case 'searching':
    case 'confirming':
    case 'submitting':
      return emptyFragment();
    case 'ready':
      // Advanced Settings opens in a popover outside the dialog. The course
      // catalog and the expanded "+N more" chips stay inside the body, which
      // scrolls when they need more room than the frame has.
      return (
        <div className={frameClassName}>
          {/*
           * -mx-1 px-1 widens the scrolling area to the modal's own padding,
           * so focus rings and shadows at its sides are not cut off.
           */}
          <div className="-mx-1 min-h-0 flex-1 overflow-y-auto px-1">
            <ParticipantAccessToggle
              isPublished={isPublished}
              onTogglePublished={onTogglePublished}
              isPublishing={isPublishing}
            />

            <hr className="border-border mb-8" />

            <ShareMethodSelector shareBy={shareBy} onChange={setShareBy} />
            <ShareTypeSelector
              shareType={shareType}
              onChange={handleShareTypeChange}
              programs={shareStore.state.learningCollectionResource.payload.programs}
              programShareDisabled={
                shareStore.state.learningCollectionResource.payload.programs.length === 0
              }
              shareBy={shareBy}
              selectedProgramFamilyId={selectedProgramFamilyId}
              onProgramFamilyChange={handleProgramFamilyChange}
            />

            {shareBy === 'url' &&
            hasShareTarget(shareStore.state.learningCollectionResource.payload.programs) ? (
              <ShareUrl />
            ) : null}
          </div>

          {/*
           * Outside the scrolling body, so the Done (URL flow) and Next
           * (Email flow) buttons sit at the bottom of the dialog whatever is
           * above them. DVLP-7559.
           */}
          <div className="flex flex-none justify-end pt-6">
            {shareBy === 'url' ? (
              <DoneButton isPublished={isPublished} isPublishing={isPublishing} close={close} />
            ) : (
              <NextButton
                collectionId={collectionId}
                isPublished={isPublished}
                isPublishing={isPublishing}
                shareBy={shareBy}
                shareType={shareType}
                returnPath={returnPath}
              />
            )}
          </div>
        </div>
      );
    case 'error':
      return (
        <div className={frameClassName}>
          <div className="bg-red-50 border border-red-600 rounded p-4 mb-4">
            <p className="text-sm text-red-600">
              <T kind={shareStore.state.message} />
            </p>
          </div>
        </div>
      );
    default:
      assertNever(shareStore.state);
  }
};

export default observer(Content);
